import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { ServiceDataFields } from '@app/modules/service-data/components/service-data/service-data-form/field-sets/service-data-fields.enum';
import { momentToNgbDate } from '@core/utils/core.util';
import { NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { ValidationErrorComponent } from '@shared/components/validation-error/validation-error.component';
import { InputValidationStatusDirective } from '@shared/directives/input-validation-status.directive';
import { controlContainerProvider } from '@shared/helpers';
import moment from 'moment';
import { combineLatest, startWith } from 'rxjs';

@Component({
  selector: 'app-service-data-form-gestation-age',
  imports: [
    ValidationErrorComponent,
    NgbDatepickerModule,
    ReactiveFormsModule,
    TranslateModule,
    InputValidationStatusDirective,
  ],
  templateUrl: './service-data-form-gestation-age.component.html',
  viewProviders: [controlContainerProvider],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceDataFormGestationAgeComponent implements OnInit {
  private controlContainer = inject(ControlContainer);
  private cdRef = inject(ChangeDetectorRef);

  @Input({ required: true }) fieldSet: string[];
  protected get form() {
    return this.controlContainer.control as FormGroup;
  }

  protected minDate = moment().subtract(1, 'year');
  protected maxDate = moment().add(1, 'year');
  protected startDate = moment().add(210, 'days');

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    const gestationAgeAutoCalculateControl = this.form.get(
      'isGestationAgeAutomaticallyCalculated'
    );
    const expectedConfinementDateControl = this.form.get(
      'expectedConfinementDate'
    );

    combineLatest([
      gestationAgeAutoCalculateControl.valueChanges.pipe(
        startWith(gestationAgeAutoCalculateControl.value)
      ),
      expectedConfinementDateControl.valueChanges.pipe(
        startWith(expectedConfinementDateControl.value)
      ),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([isGestationAgeAutoCalculated, expectedConfinementDate]) =>
        this.calculateGestationAge(
          isGestationAgeAutoCalculated,
          expectedConfinementDate
        )
      );
  }

  private calculateGestationAge(
    isGestationAgeAutoCalculated: boolean,
    expectedConfinementDate: Date
  ) {
    if (!isGestationAgeAutoCalculated || !expectedConfinementDate) {
      return;
    }

    const dateNow = moment();
    const startOfPregnancy = moment(expectedConfinementDate).subtract(280, 'd');

    const fullGestationDays = dateNow.diff(startOfPregnancy, 'd');
    const week = Math.floor(fullGestationDays / 7);
    const day = fullGestationDays % 7;

    this.form.get('gestationAge').patchValue({ day, week });
    this.form.get('gestationAge').markAllAsTouched();
    this.cdRef.markForCheck();
  }

  protected readonly ServiceDataFields = ServiceDataFields;
  protected readonly momentToNgbDate = momentToNgbDate;
}
